import { computed, ref } from 'vue'
import api from '../services/api'
import { getPlanFeaturesFromApi, hasPlanFeature, normalizePlanoCodigo } from '../constants/planFeatures'
import { setOwnerAccessBlocked } from './useAccountAccessBlock'

const workspaceCache = new Map()
const meuPlanoCache = ref(null)

export function invalidatePlanoFeaturesCache() {
  workspaceCache.clear()
  meuPlanoCache.value = null
}

export function usePlanoFeatures() {
  const loading = ref(false)
  const planoCodigo = ref('FREE')
  const planoNome = ref('Free')
  const features = ref(getPlanFeaturesFromApi(null))
  const ownerUserId = ref(null)
  const ownerAccessBlocked = ref(false)
  const limits = ref({})
  const can = (featureKey) => hasPlanFeature(features.value, featureKey)

  const loadMeuPlano = async () => {
    loading.value = true
    try {
      const { data } = await api.get('/planos/meu-plano')
      meuPlanoCache.value = data
      planoCodigo.value = normalizePlanoCodigo(data?.codigo)
      planoNome.value = data?.nome || 'Free'
      features.value = getPlanFeaturesFromApi(data?.features)
      limits.value = {
        maxWorkspacesTask: data?.maxWorkspacesTask,
        maxWorkspacesCrm: data?.maxWorkspacesCrm,
        maxUsersPerWorkspace: data?.maxUsersPerWorkspace
      }
      return data
    } finally {
      loading.value = false
    }
  }

  /** Features/limites do plano do DONO do workspace (convidados herdam; plano pessoal do membro não aplica aqui). */
  const loadForWorkspace = async (workspaceId, force = false) => {
    if (!workspaceId) return null
    const key = String(workspaceId)
    if (!force && workspaceCache.has(key)) {
      const cached = workspaceCache.get(key)
      applyWorkspacePayload(cached)
      return cached
    }
    loading.value = true
    try {
      const { data } = await api.get(`/planos/workspace/${workspaceId}/features`)
      workspaceCache.set(key, data)
      applyWorkspacePayload(data)
      return data
    } finally {
      loading.value = false
    }
  }

  const applyWorkspacePayload = (data) => {
    if (!data) return
    planoCodigo.value = normalizePlanoCodigo(data.planoCodigo)
    planoNome.value = data.planoNome || planoCodigo.value
    features.value = getPlanFeaturesFromApi(data.features)
    limits.value = data.limits || {}
    ownerUserId.value = data.ownerUserId ?? null
    ownerAccessBlocked.value = Boolean(data.ownerAccessBlocked)
    setOwnerAccessBlocked(ownerAccessBlocked.value)
  }

  const invalidateWorkspace = (workspaceId) => {
    if (workspaceId) workspaceCache.delete(String(workspaceId))
  }

  const invalidateAll = () => {
    invalidatePlanoFeaturesCache()
  }

  const requiresUpgrade = (featureKey) => !can(featureKey)

  const isProOrAbove = computed(() =>
    ['PRO_VERIFICADO', 'CORPORATIVO'].includes(planoCodigo.value)
  )

  return {
    loading,
    planoCodigo,
    planoNome,
    features,
    limits,
    ownerUserId,
    ownerAccessBlocked,
    can,
    requiresUpgrade,
    isProOrAbove,
    loadMeuPlano,
    loadForWorkspace,
    invalidateWorkspace,
    invalidateAll
  }
}
